// src/components/button.jsx
import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import React, { forwardRef } from 'react';
import { Link } from './link';

/**
 * Scrimly Button – Tailwind v1 style
 * -------------------------------------------------
 * Variants: primary (bright blue), default (alias of primary), black, white, outline, ghost, link, destructive
 * Sizes: xs, sm (default), md, lg
 */

// inject CSS vars (greens for legacy + brand blues)
(function ensureBrandCSS() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('scrimly-brand-vars')) return;

  const css = `
  :root{
    /* legacy greens */
    --scrimly-500:#22C55E; --scrimly-600:#16A34A;

    /* brand palette */
    --brand-dark:#252C3E;
    --brand-blue:#3483FE;
    --brand-blueLight:#4D95FE;
    --brand-accent:#82BEFF;
    --brand-gray:#C2C4C9;
  }`;
  const tag = document.createElement('style');
  tag.id = 'scrimly-brand-vars';
  tag.appendChild(document.createTextNode(css));
  document.head.appendChild(tag);
})();

// Base styles
const base =
  'relative isolate inline-flex items-center justify-center gap-1.5 ' +
  'font-normal transition-colors rounded ' +
  'focus:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-[color:var(--brand-accent)]/70 ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-white ' +
  'dark:focus-visible:ring-offset-neutral-900 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

// Sizes
const sizes = {
  xs: 'text-xs py-1.5 px-2.5 rounded',
  sm: 'text-sm py-2 px-4 rounded',
  md: 'text-sm py-2.5 px-5 rounded',
  lg: 'text-base py-3 px-6 rounded',
};

const iconSize = { xs: 'size-[14px]', sm: 'size-4', md: 'size-[18px]', lg: 'size-5' };

// Variants
//
// Updated May 2026 from the legacy blue (#3483FE / #4D95FE) brand to the
// lime/pitch design system used everywhere else in the app:
//   - Lime accent #b5f03d on pitch-dark text #0d1f14
//   - Hover slightly darker lime #94cc28
//   - Moss green #5a8a1f for link text accents
// All <Button color="blue"> and <Button color="green"> calls across the
// app fall through to `primary` here, so this single change limes every
// previously-blue button. Outline, link, and ghost updated to match.
const variants = {
  primary: 'text-[#0d1f14] bg-[#b5f03d] hover:bg-[#94cc28]',
  default: 'text-[#0d1f14] bg-[#b5f03d] hover:bg-[#94cc28]', // alias
  black: 'text-white bg-[#0d1f14] hover:bg-[#162b1e]',
  white: 'text-[#0d1f14] bg-white border border-zinc-300 hover:bg-zinc-50 shadow-sm',

  outline:
    'bg-white text-[#0d1f14] border-2 border-[#b5f03d] ' +
    'hover:bg-[#b5f03d]/10 active:bg-[#b5f03d]/15 ' +
    '[@media(pointer:fine)]:hover:shadow-[0_0_0_3px_rgba(181,240,61,0.25)]',

  ghost:
    'bg-transparent text-[#0d1f14] hover:bg-zinc-100/70',

  link:
    'bg-transparent p-0 h-auto text-[#5a8a1f] hover:text-[#0d1f14] underline underline-offset-4 shadow-none',

  destructive: 'text-white bg-rose-600 hover:bg-rose-700',
};

// Spinner
function Spinner({ className }) {
  return (
    <svg className={clsx('animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-90" d="M4 12a8 8 0 0 1 8-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

// Variant resolver
function resolveVariant({ variant, outline, plain, color }) {
  if (outline) return 'outline';
  const v = (variant || '').toLowerCase();
  if (v === 'default') return 'primary';
  if (v && variants[v]) return v;
  if (plain) return 'ghost';
  if (['green', 'scrimly', 'blue'].includes(color)) return 'primary';
  if (['black', 'dark'].includes(color)) return 'black';
  if (['white', 'light'].includes(color)) return 'white';
  return 'primary';
}

/** Main Button */
export const Button = forwardRef(function Button(
  {
    as: Comp,
    variant,
    size = 'sm',
    color,
    outline,
    plain,
    loading = false,
    disabled,
    className,
    leftIcon,
    rightIcon,
    showIcons = false,
    children,
    fullWidth = false,
    ...props
  },
  ref
) {
  const resolvedVariant = resolveVariant({ variant, outline, plain, color });
  const classes = clsx(
    base,
    sizes[size],
    variants[resolvedVariant],
    fullWidth && 'w-full',
    !children && resolvedVariant !== 'link' && '!px-0 !py-0 w-9 h-9 rounded',
    className
  );
  const isDisabled = disabled || loading;
  const RenderComp = Comp ?? (('href' in props && props.href) ? Link : Headless.Button);
  const commonProps = {
    ref,
    className: classes,
    ...(RenderComp === Headless.Button
      ? { disabled: isDisabled }
      : { 'aria-disabled': isDisabled || undefined }),
    ...props,
  };
  const content = (
    <TouchTarget>
      {loading ? (
        <Spinner className={clsx(iconSize[size])} />
      ) : (
        <>
          {showIcons && leftIcon && <span className={clsx(iconSize[size])}>{leftIcon}</span>}
          {children && <span className="truncate">{children}</span>}
          {showIcons && rightIcon && <span className={clsx(iconSize[size])}>{rightIcon}</span>}
        </>
      )}
    </TouchTarget>
  );
  return <RenderComp {...commonProps}>{content}</RenderComp>;
});

/** Icon-only button */
export const IconButton = forwardRef(function IconButton(
  { as: Comp, variant = 'ghost', size = 'sm', label, className, children, ...props },
  ref
) {
  return (
    <Button
      ref={ref}
      as={Comp}
      variant={variant}
      size={size}
      className={clsx('!p-0 !w-9 !h-9 rounded', className)}
      aria-label={label}
      showIcons
      {...props}
    >
      {children}
    </Button>
  );
});

/** Button group */
export function ButtonGroup({ className, children }) {
  return (
    <div className={clsx('inline-flex', className)}>
      {React.Children.map(children, (child, i, arr) => {
        if (!React.isValidElement(child)) return child;
        const isFirst = i === 0;
        const isLast = i === arr.length - 1;
        return React.cloneElement(child, {
          className: clsx(
            child.props.className,
            'rounded-none',
            isFirst && 'rounded-l',
            isLast && 'rounded-r',
          ),
        });
      })}
    </div>
  );
}

/** Touch target */
export function TouchTarget({ children }) {
  return (
    <>
      <span
        className="absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  );
}
